import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, Users, MessageCircle, CreditCard, Eye, 
  MousePointerClick, Download, Star, TrendingUp, 
  Loader2, BarChart3, Crown, UserPlus, MessagesSquare
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, sub, color = 'purple-light', testId }) {
  return (
    <div className="glass rounded-xl p-5 border border-white/5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg bg-${color}/20 flex items-center justify-center`}>
          <Icon size={18} className={`text-${color}`} />
        </div>
        <span className="text-velvet_text-secondary text-xs uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-velvet_text-primary">{value}</p>
      {sub && <p className="text-velvet_text-secondary text-xs mt-1">{sub}</p>}
    </div>
  );
}

function CharacterBar({ name, count, maxCount }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-velvet_text-secondary text-sm w-24 truncate capitalize">{name}</span>
      <div className="flex-1 h-6 bg-white/5 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-purple-main to-fuchsia-main rounded-full flex items-center justify-end pr-2 transition-all duration-500"
          style={{ width: `${Math.max(pct, 8)}%` }}
        >
          <span className="text-white text-xs font-bold">{count}</span>
        </div>
      </div>
    </div>
  );
}

function CtaBar({ name, clicks, maxClicks }) {
  const pct = maxClicks > 0 ? (clicks / maxClicks) * 100 : 0;
  const labels = { hero: 'Hero CTA', header: 'Header', premium: 'Premium', final: 'Final CTA', install: 'Install' };
  return (
    <div className="flex items-center gap-3">
      <span className="text-velvet_text-secondary text-sm w-24 truncate">{labels[name] || name}</span>
      <div className="flex-1 h-5 bg-white/5 rounded-full overflow-hidden">
        <div 
          className="h-full bg-gradient-to-r from-green-600 to-emerald-400 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
          style={{ width: `${Math.max(pct, 8)}%` }}
        >
          <span className="text-white text-xs font-bold">{clicks}</span>
        </div>
      </div>
    </div>
  );
}

function MiniChart({ data }) {
  if (!data || data.length === 0) return <p className="text-velvet_text-secondary text-xs">Pas de données</p>;
  const maxVal = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="flex items-end gap-[3px] h-20">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
          <div 
            className="w-full bg-gradient-to-t from-purple-main to-fuchsia-main rounded-t opacity-80 hover:opacity-100 transition-opacity min-h-[2px]"
            style={{ height: `${(d.count / maxVal) * 100}%` }}
          />
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 glass rounded px-1.5 py-0.5 text-[10px] text-velvet_text-primary opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {d.date?.slice(5)} : {d.count}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function AdminStatsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || user?.email !== 'test@velvet.ai') {
      navigate('/');
      return;
    }
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch stats');
        const data = await res.json();
        setStats(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-light" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <p className="text-red-400">Erreur : {error}</p>
      </div>
    );
  }

  const { users, conversations, revenue, landing_page, reviews } = stats;
  const maxCharCount = conversations.popular_characters.length > 0 
    ? conversations.popular_characters[0].conversations : 1;
  const maxCta = landing_page.cta_details.length > 0 
    ? Math.max(...landing_page.cta_details.map(c => c.clicks)) : 1;

  return (
    <div className="min-h-screen bg-velvet-bg" data-testid="admin-stats-page">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-velvet_text-secondary hover:text-velvet_text-primary transition-colors" data-testid="stats-back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <BarChart3 size={20} className="text-purple-light" />
            <h1 className="font-heading text-lg sm:text-xl gradient-text">Dashboard Analytics</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ===== USERS SECTION ===== */}
        <section>
          <h2 className="text-velvet_text-primary font-heading text-base sm:text-lg mb-4 flex items-center gap-2">
            <Users size={18} className="text-purple-light" /> Utilisateurs
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard icon={Users} label="Total inscrits" value={users.total} testId="stat-users-total" />
            <StatCard icon={UserPlus} label="Invités" value={users.guests} color="blue-400" testId="stat-guests" />
            <StatCard icon={TrendingUp} label="Aujourd'hui" value={users.today} color="green-400" testId="stat-today" />
            <StatCard icon={TrendingUp} label="Cette semaine" value={users.this_week} color="yellow-400" testId="stat-week" />
            <StatCard icon={TrendingUp} label="Ce mois" value={users.this_month} color="fuchsia-main" testId="stat-month" />
          </div>
          {/* Signups chart */}
          {users.signups_chart.length > 0 && (
            <div className="glass rounded-xl p-5 mt-3 border border-white/5">
              <p className="text-velvet_text-secondary text-xs uppercase tracking-wider mb-3">Inscriptions — 30 derniers jours</p>
              <MiniChart data={users.signups_chart} />
            </div>
          )}
        </section>

        {/* ===== CONVERSATIONS SECTION ===== */}
        <section>
          <h2 className="text-velvet_text-primary font-heading text-base sm:text-lg mb-4 flex items-center gap-2">
            <MessageCircle size={18} className="text-purple-light" /> Conversations
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard icon={MessagesSquare} label="Conversations" value={conversations.total.toLocaleString()} testId="stat-convos" />
            <StatCard icon={MessageCircle} label="Messages totaux" value={conversations.total_messages.toLocaleString()} color="blue-400" testId="stat-messages" />
            <StatCard icon={Star} label="Note moyenne" value={`${reviews.avg_rating}/5`} sub={`${reviews.total} avis`} color="yellow-400" testId="stat-rating" />
          </div>
          {/* Popular characters */}
          {conversations.popular_characters.length > 0 && (
            <div className="glass rounded-xl p-5 mt-3 border border-white/5">
              <p className="text-velvet_text-secondary text-xs uppercase tracking-wider mb-4">Personnages populaires</p>
              <div className="space-y-2.5">
                {conversations.popular_characters.map(ch => (
                  <CharacterBar key={ch.character_id} name={ch.character_id} count={ch.conversations} maxCount={maxCharCount} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ===== REVENUE SECTION ===== */}
        <section>
          <h2 className="text-velvet_text-primary font-heading text-base sm:text-lg mb-4 flex items-center gap-2">
            <CreditCard size={18} className="text-purple-light" /> Revenus
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <StatCard icon={CreditCard} label="Revenu total" value={`${revenue.total.toLocaleString()} €`} color="green-400" testId="stat-revenue" />
            <StatCard icon={Crown} label="Transactions" value={revenue.total_transactions} sub={`${revenue.pending_transactions} en attente`} testId="stat-transactions" />
          </div>
          {/* Revenue breakdown */}
          {revenue.breakdown.length > 0 && (
            <div className="glass rounded-xl p-5 mt-3 border border-white/5">
              <p className="text-velvet_text-secondary text-xs uppercase tracking-wider mb-3">Répartition par type</p>
              <div className="space-y-2">
                {revenue.breakdown.map(r => (
                  <div key={r.type} className="flex items-center justify-between text-sm">
                    <span className="text-velvet_text-secondary capitalize">{r.type}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-velvet_text-secondary">{r.count} achats</span>
                      <span className="text-velvet_text-primary font-medium">{r.revenue.toLocaleString()} €</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ===== LANDING PAGE ANALYTICS ===== */}
        <section>
          <h2 className="text-velvet_text-primary font-heading text-base sm:text-lg mb-4 flex items-center gap-2">
            <Eye size={18} className="text-purple-light" /> Landing Page /download
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard icon={Eye} label="Visites totales" value={landing_page.visits_total} testId="stat-visits" />
            <StatCard icon={Eye} label="Aujourd'hui" value={landing_page.visits_today} color="green-400" testId="stat-visits-today" />
            <StatCard icon={Eye} label="Cette semaine" value={landing_page.visits_this_week} color="blue-400" testId="stat-visits-week" />
            <StatCard icon={MousePointerClick} label="Clics CTA" value={landing_page.cta_clicks} color="fuchsia-main" testId="stat-cta" />
            <StatCard icon={Download} label="Clics Installer" value={landing_page.install_clicks} color="green-400" testId="stat-install" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            {/* Conversion */}
            <div className="glass rounded-xl p-5 border border-white/5">
              <p className="text-velvet_text-secondary text-xs uppercase tracking-wider mb-2">Taux de conversion</p>
              <p className="text-3xl font-bold text-velvet_text-primary">{landing_page.conversion_rate}%</p>
              <p className="text-velvet_text-secondary text-xs mt-1">Visiteurs → Inscrits</p>
            </div>
            {/* CTA breakdown */}
            {landing_page.cta_details.length > 0 && (
              <div className="glass rounded-xl p-5 border border-white/5">
                <p className="text-velvet_text-secondary text-xs uppercase tracking-wider mb-3">Clics par bouton</p>
                <div className="space-y-2">
                  {landing_page.cta_details.map(c => (
                    <CtaBar key={c.button} name={c.button} clicks={c.clicks} maxClicks={maxCta} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
